import React, { useState } from 'react';
import { Subscription, PlanTier, ChurnReason, SubscriptionStatus, PlanDetail } from '../../types';
import CheckoutModal from '../CheckoutModal';
import { CURRENCIES } from '../../constants';
import {
  Sparkles,
  Zap,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  TrendingUp,
  Award,
  AlertTriangle,
  Gift,
  RotateCcw,
  Check
} from 'lucide-react';

interface PortalSubscriptionProps {
  subscription: Subscription | null;
  plans: PlanDetail[];
  onUpdate: (updates: Partial<Subscription>) => void;
  onCancel: (reason: ChurnReason, feedback: string) => void;
}

const PortalSubscription: React.FC<PortalSubscriptionProps> = ({ subscription, plans, onUpdate, onCancel }) => {
  const [isManaging, setIsManaging] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [cancelStep, setCancelStep] = useState(1);
  const [churnReason, setChurnReason] = useState<ChurnReason>('too_expensive');
  const [feedback, setFeedback] = useState('');
  const [checkoutPlan, setCheckoutPlan] = useState<PlanDetail | null>(null);
  const [billingCycleToggle, setBillingCycleToggle] = useState<'monthly' | 'yearly'>('monthly');

  const visiblePlans = plans.filter(plan => plan.isActive !== false);
  const normalizePlanRef = (value: unknown) => String(value || '').trim().toLowerCase();
  const getCurrencySymbol = (currencyCode?: string) => CURRENCIES.find(c => c.code === currencyCode)?.symbol || CURRENCIES[0].symbol;
  const formatPlanPrice = (value: unknown, plan?: PlanDetail) => {
    const amount = Number(value || 0);
    const currencyCode = plan?.currency || 'INR';
    return getCurrencySymbol(currencyCode) + (Number.isFinite(amount) ? amount.toLocaleString(currencyCode === 'INR' ? 'en-IN' : 'en-US') : '0');
  };
  const currentPlanDetails = visiblePlans.find(p => {
    const subscriptionPlan = normalizePlanRef(subscription?.plan);
    return [p.id, p.tier, (p as PlanDetail & { name?: string }).name]
      .filter(Boolean)
      .some(value => normalizePlanRef(value) === subscriptionPlan);
  });
  const currentPlanLabel = currentPlanDetails ? ((currentPlanDetails as PlanDetail & { name?: string }).name || currentPlanDetails.tier) : String(subscription?.plan || 'Current Plan');

  if (!subscription) {
    return (
      <div className="space-y-8 animate-in fade-in duration-500">
        {/* Value & Upgrade Hero Banner */}
        <div className="bg-gradient-to-br from-indigo-900 via-slate-900 to-indigo-950 rounded-[2.5rem] p-10 sm:p-12 text-white shadow-2xl relative overflow-hidden">
          <div className="relative z-10 max-w-2xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 text-[10px] font-black uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
              Unlock High-Performance Scale
            </div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-white">
              Choose the Perfect Plan for Your Growth
            </h2>
            <p className="text-sm font-medium text-slate-300 leading-relaxed">
              Activate instant access to high-capacity AI workloads, dedicated workspace isolation, and automated billing features. Switch or cancel anytime with zero friction.
            </p>

            {/* Annual Billing Conversion Incentive */}
            <div className="pt-2 flex items-center gap-3">
              <div className="flex bg-slate-800/80 p-1 rounded-2xl border border-white/10 w-fit">
                <button
                  onClick={() => setBillingCycleToggle('monthly')}
                  className={`px-4 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                    billingCycleToggle === 'monthly' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycleToggle('yearly')}
                  className={`px-4 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                    billingCycleToggle === 'yearly' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <span>Yearly</span>
                  <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded-md font-bold text-white">Save 20%</span>
                </button>
              </div>
              <span className="text-xs text-emerald-400 font-bold hidden sm:inline-flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> 2 Months Free on Annual
              </span>
            </div>
          </div>

          <div className="absolute -right-12 -bottom-12 opacity-10 pointer-events-none">
            <Zap className="w-96 h-96 text-indigo-400" />
          </div>
        </div>

        {/* Pricing Cards with Conversion Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {visiblePlans.map((plan, index) => {
            const isPopular = index === 1 || plan.tier.toLowerCase().includes('pro') || plan.tier.toLowerCase().includes('growth');
            const monthlyPrice = Number(plan.price || 0);
            const calculatedPrice = billingCycleToggle === 'yearly' ? Math.round(monthlyPrice * 0.8) : monthlyPrice;

            return (
              <div
                key={plan.id}
                className={`p-8 rounded-[2.5rem] border-2 bg-white flex flex-col justify-between transition-all duration-300 relative ${
                  isPopular
                    ? 'border-indigo-600 shadow-2xl shadow-indigo-100 ring-4 ring-indigo-50'
                    : 'border-slate-100 hover:border-slate-300 shadow-sm'
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-indigo-600 to-indigo-700 text-white text-[10px] font-black uppercase tracking-widest shadow-md flex items-center gap-1">
                    <Award className="w-3 h-3" /> Most Popular Choice
                  </div>
                )}

                <div>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className="px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest bg-slate-100 text-slate-700">
                        {plan.tier}
                      </span>
                      <p className="text-xs text-slate-400 font-medium mt-2">
                        {plan.description || 'Designed for high-growth SaaS teams.'}
                      </p>
                    </div>
                  </div>

                  <div className="my-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-black text-slate-900">{formatPlanPrice(calculatedPrice, plan)}</span>
                      <span className="text-xs font-bold text-slate-400 uppercase">/ month</span>
                    </div>
                    {billingCycleToggle === 'yearly' && (
                      <p className="text-[11px] font-bold text-emerald-600 mt-1">
                        Billed annually (saves {formatPlanPrice((monthlyPrice * 12 * 0.2).toFixed(0), plan)}/yr)
                      </p>
                    )}
                  </div>

                  <div className="pt-4 border-t border-slate-100 mb-8 space-y-3">
                    <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                      Included Superpowers:
                    </p>
                    {(plan.features || []).slice(0, 5).map((feature, fIdx) => (
                      <div key={fIdx} className="text-xs font-semibold text-slate-700 flex items-start gap-2.5">
                        <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => setCheckoutPlan(plan)}
                    className={`w-full py-4 rounded-2xl font-black text-xs uppercase tracking-wider shadow-lg flex items-center justify-center gap-2 transition-all active:scale-95 cursor-pointer ${
                      isPopular
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-200'
                        : 'bg-slate-900 text-white hover:bg-black'
                    }`}
                  >
                    <span>Activate {plan.tier} Plan</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="text-center text-[10px] font-bold text-slate-400">
                    Instant access • 14-day money-back guarantee
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {checkoutPlan && (
          <div className="fixed inset-0 z-[100] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-3xl bg-white rounded-[2.5rem] p-8 shadow-2xl max-h-[90vh] overflow-y-auto relative">
              <button
                type="button"
                onClick={() => setCheckoutPlan(null)}
                className="absolute top-5 right-5 w-10 h-10 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900 flex items-center justify-center transition-all z-10"
                aria-label="Close checkout"
              >
                ×
              </button>

              <CheckoutModal
                plan={checkoutPlan}
                billingCycle={billingCycleToggle}
                onClose={() => setCheckoutPlan(null)}
                onSuccess={() => {
                  onUpdate({ plan: checkoutPlan.tier as PlanTier, mrr: checkoutPlan.price });
                  setCheckoutPlan(null);
                  setIsManaging(false);
                }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  const handleFinalCancel = () => {
    onCancel(churnReason, feedback);
    setIsCanceling(false);
  };

  const handlePause = (months: number) => {
    const pauseUntil = new Date();
    pauseUntil.setMonth(pauseUntil.getMonth() + months);
    onUpdate({ status: SubscriptionStatus.PAUSED, pausedUntil: pauseUntil.toISOString().split('T')[0] });
    setIsPausing(false);
  };

  const handleScheduleChange = (targetPlan: PlanTier) => {
    onUpdate({
      scheduledChange: {
        targetPlan,
        effectiveDate: subscription.nextBillingDate,
        action: 'downgrade'
      }
    });
    setIsManaging(false);
  };

  // Retention & Cancellation Flow
  if (isCanceling) {
    return (
      <div className="bg-white rounded-[2.5rem] p-8 sm:p-12 border border-slate-200 shadow-2xl animate-in zoom-in-95 max-w-3xl mx-auto">
        {cancelStep === 1 && (
          <div className="space-y-8">
            <div className="text-center space-y-2">
              <div className="w-14 h-14 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="w-7 h-7" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                Before you go, let us make this right.
              </h2>
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                We're committed to your team's success. Tell us what we can do better or choose a tailored plan adjustment below.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { id: 'too_expensive', label: 'Need a more flexible budget option', icon: '💰' },
                { id: 'missing_features', label: 'Looking for a specific feature', icon: '🛠️' },
                { id: 'temporary', label: 'Project paused / Temporary inactivity', icon: '⏸️' },
                { id: 'switching', label: 'Exploring other solutions', icon: '🔄' },
              ].map(r => (
                <button
                  key={r.id}
                  onClick={() => setChurnReason(r.id as ChurnReason)}
                  className={`p-5 rounded-3xl border-2 text-left transition-all flex items-center gap-4 cursor-pointer ${
                    churnReason === r.id
                      ? 'border-indigo-600 bg-indigo-50 shadow-md'
                      : 'border-slate-100 hover:border-slate-200'
                  }`}
                >
                  <span className="text-2xl">{r.icon}</span>
                  <span className="font-bold text-xs text-slate-900">{r.label}</span>
                </button>
              ))}
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                How can we better support your workflow? (Optional)
              </label>
              <textarea
                placeholder="Tell us what you loved or what we could improve..."
                className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-medium text-slate-800 outline-none focus:ring-4 focus:ring-indigo-600/10 min-h-[100px]"
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={() => setIsCanceling(false)}
                className="flex-1 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-xs uppercase tracking-wider shadow-lg transition-all"
              >
                Keep My Active Plan & Data
              </button>
              <button
                onClick={() => setCancelStep(2)}
                className="flex-1 py-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl font-black text-xs uppercase tracking-wider transition-all"
              >
                See Retention Options
              </button>
            </div>
          </div>
        )}

        {cancelStep === 2 && (
          <div className="space-y-8 text-center">
            <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
              <Gift className="w-8 h-8" />
            </div>

            <div className="space-y-2">
              <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-black uppercase tracking-wider">
                Exclusive Loyalty Benefit
              </span>
              <h2 className="text-3xl font-black text-slate-900 tracking-tight">
                Claim 50% Off Your Next 3 Months!
              </h2>
              <p className="text-sm text-slate-500 max-w-lg mx-auto">
                Stay with us and keep all your active workspace configurations, high-throughput quotas, and dedicated team seats for half the price.
              </p>
            </div>

            <div className="p-6 bg-slate-50 rounded-3xl border border-slate-200/80 max-w-md mx-auto text-left space-y-2.5">
              <div className="text-xs font-bold text-slate-800 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>Zero service disruption or data migration needed</span>
              </div>
              <div className="text-xs font-bold text-slate-800 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>Discount applies automatically to your next 3 invoices</span>
              </div>
              <div className="text-xs font-bold text-slate-800 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>Upgrade or modify anytime with prorated credit</span>
              </div>
            </div>

            <div className="space-y-3 max-w-md mx-auto">
              <button
                onClick={() => {
                  onUpdate({ mrr: (subscription.mrr || 49) * 0.5 });
                  setIsCanceling(false);
                  alert('🎉 50% Loyalty Discount applied to your subscription!');
                }}
                className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl transition-all hover:scale-[1.02] cursor-pointer"
              >
                Claim 50% Discount & Stay
              </button>

              <button
                onClick={() => {
                  setIsCanceling(false);
                  setIsPausing(true);
                }}
                className="w-full py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-2xl font-bold text-xs uppercase tracking-wider transition-all cursor-pointer"
              >
                Snooze Subscription Instead (Save All Data)
              </button>

              <button
                onClick={handleFinalCancel}
                className="w-full py-3 text-slate-400 font-bold text-xs uppercase tracking-wider hover:text-rose-600 transition-colors cursor-pointer"
              >
                Proceed with account cancellation
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Active Subscription Status Banner */}
      <div className="bg-white rounded-[2.5rem] p-8 sm:p-10 border border-slate-200 shadow-sm relative overflow-hidden group">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8 relative z-10">
          <div className="flex gap-6 items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-indigo-100">
              <Zap className="w-8 h-8" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  Active & Healthy
                </span>
                <span className="text-xs font-bold text-slate-400 font-mono">
                  ID: {subscription.id}
                </span>
              </div>
              <h2 className="text-3xl font-black text-slate-900 tracking-tight">
                {currentPlanLabel}
              </h2>
              <p className="text-xs font-semibold text-slate-500 mt-1">
                Billed {subscription.billingCycle} • Next charge of <strong>{formatPlanPrice(subscription.mrr, currentPlanDetails)}</strong> on <strong>{subscription.nextBillingDate}</strong>
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
            <button
              onClick={() => setIsManaging(!isManaging)}
              className="flex-1 lg:flex-none px-6 py-3.5 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-wider shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
            >
              <TrendingUp className="w-4 h-4" />
              {isManaging ? 'Close Upgrade Menu' : 'Upgrade / Change Plan'}
            </button>
            <button
              onClick={() => setIsPausing(true)}
              className="flex-1 lg:flex-none px-5 py-3.5 bg-slate-50 border border-slate-200 text-slate-700 rounded-2xl font-black text-xs uppercase tracking-wider hover:bg-slate-100 transition-all active:scale-95 cursor-pointer"
            >
              Snooze
            </button>
            <button
              onClick={() => setIsCanceling(true)}
              className="flex-1 lg:flex-none px-5 py-3.5 bg-white border border-slate-200 text-slate-400 rounded-2xl font-black text-xs uppercase tracking-wider hover:text-rose-600 hover:border-rose-200 transition-all active:scale-95 cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Annual Upgrade Banner Inside Active Card */}
        {subscription.billingCycle === 'monthly' && (
          <div className="mt-8 p-5 bg-gradient-to-r from-emerald-50 via-teal-50 to-indigo-50 rounded-2xl border border-emerald-200/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-600 text-white rounded-xl shadow-sm">
                <Gift className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">
                  Switch to Annual & Save 20% Instantly
                </h4>
                <p className="text-xs text-slate-600 mt-0.5">
                  Lock in 2 months free and simplify your expense accounting with annual billing.
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                onUpdate({ billingCycle: 'yearly', mrr: Math.round((subscription.mrr || 49) * 0.8) });
                alert('🎉 Successfully switched to Annual Billing! 20% discount applied.');
              }}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-sm transition-all shrink-0 cursor-pointer"
            >
              Switch & Save 20%
            </button>
          </div>
        )}

        {subscription.scheduledChange && (
          <div className="mt-6 p-4 bg-amber-50 rounded-2xl border border-amber-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse"></div>
              <p className="text-xs font-bold text-amber-900">
                Scheduled Change: Transitioning to <strong>{subscription.scheduledChange.targetPlan}</strong> on {subscription.scheduledChange.effectiveDate}.
              </p>
            </div>
            <button
              onClick={() => onUpdate({ scheduledChange: undefined })}
              className="text-[10px] font-black text-amber-700 uppercase hover:underline cursor-pointer"
            >
              Keep Current Plan
            </button>
          </div>
        )}
      </div>

      {/* Snooze / Pause Modal */}
      {isPausing && (
        <div className="bg-slate-900 rounded-[2.5rem] p-8 sm:p-10 text-white animate-in slide-in-from-top-4 shadow-xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 bg-indigo-500/20 text-indigo-400 rounded-xl">
              <RotateCcw className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-black uppercase tracking-tight">Need a temporary break?</h3>
          </div>
          <p className="text-slate-300 text-xs mb-6 max-w-xl">
            Snooze billing for up to 3 months. All your data, workspace isolation settings, and custom models remain 100% safe and resume automatically.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[1, 2, 3].map(m => (
              <button
                key={m}
                onClick={() => handlePause(m)}
                className="py-3.5 bg-white/10 hover:bg-indigo-600 hover:text-white border border-white/10 rounded-2xl font-black text-xs uppercase tracking-wider transition-all cursor-pointer"
              >
                Snooze for {m} Month{m > 1 ? 's' : ''}
              </button>
            ))}
          </div>
          <button
            onClick={() => setIsPausing(false)}
            className="mt-5 text-[10px] font-black uppercase text-slate-400 hover:text-white transition-colors cursor-pointer"
          >
            Cancel & Return
          </button>
        </div>
      )}

      {/* Plan Upgrade & Comparison Grid */}
      {isManaging && (
        <div className="space-y-4 animate-in slide-in-from-top-4 duration-500">
          <div className="flex items-center justify-between px-2">
            <div>
              <h3 className="text-base font-black text-slate-900 uppercase tracking-wider">
                Select Your Desired Growth Tier
              </h3>
              <p className="text-xs text-slate-500">
                Prorated immediately. Unlocked quotas and features activate instantly.
              </p>
            </div>
            <span className="text-[10px] font-black uppercase text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-200">
              Prorated Upgrade
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {visiblePlans.map(plan => {
              const isCurrent = [plan.id, plan.tier, (plan as PlanDetail & { name?: string }).name]
                .filter(Boolean)
                .some(value => normalizePlanRef(value) === normalizePlanRef(subscription.plan));
              return (
                <div
                  key={plan.id}
                  className={`p-8 rounded-[2.5rem] border-2 flex flex-col justify-between transition-all duration-200 ${
                    isCurrent
                      ? 'border-indigo-600 bg-indigo-50/40 shadow-lg'
                      : 'border-slate-200 bg-white hover:border-slate-300 shadow-sm'
                  }`}
                >
                  <div>
                    <div className="flex justify-between items-start mb-4">
                      <span
                        className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${
                          isCurrent ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {isCurrent ? 'Active Plan' : plan.tier}
                      </span>
                      <p className="text-2xl font-black text-slate-900">{formatPlanPrice(plan.price, plan)}<span className="text-xs font-normal text-slate-400">/mo</span></p>
                    </div>

                    <p className="text-xs text-slate-500 mb-6 font-medium">
                      {plan.description || 'Full-stack billing and compute power.'}
                    </p>

                    <div className="space-y-2.5 mb-8">
                      {plan.features.slice(0, 4).map((f, i) => (
                        <div key={i} className="text-xs font-medium text-slate-700 flex items-start gap-2">
                          <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                          <span>{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {!isCurrent ? (
                    <div className="space-y-2">
                      <button
                        onClick={() => setCheckoutPlan(plan)}
                        className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-xs uppercase tracking-wider shadow-lg shadow-indigo-100 transition-all flex items-center justify-center gap-2 cursor-pointer"
                      >
                        <Zap className="w-3.5 h-3.5" />
                        Upgrade to {plan.tier} Now
                      </button>
                      <button
                        onClick={() => handleScheduleChange(plan.tier as PlanTier)}
                        className="w-full py-2.5 bg-white border border-slate-200 hover:bg-slate-50 rounded-2xl font-bold text-[10px] text-slate-600 uppercase tracking-wider transition-all cursor-pointer"
                      >
                        Schedule for Next Cycle
                      </button>
                    </div>
                  ) : (
                    <div className="py-3 text-center rounded-2xl bg-indigo-100/50 text-indigo-700 font-black text-xs uppercase tracking-wider">
                      Current Plan
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {checkoutPlan && (
        <div className="fixed inset-0 z-[100] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-3xl bg-white rounded-[2.5rem] p-8 shadow-2xl max-h-[90vh] overflow-y-auto relative">
            <button
              type="button"
              onClick={() => setCheckoutPlan(null)}
              className="absolute top-5 right-5 w-10 h-10 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900 flex items-center justify-center transition-all z-10"
              aria-label="Close checkout"
            >
              ×
            </button>

            <CheckoutModal
              plan={checkoutPlan}
              billingCycle={billingCycleToggle}
              onClose={() => setCheckoutPlan(null)}
              onSuccess={() => {
                onUpdate({ plan: checkoutPlan.tier as PlanTier, mrr: checkoutPlan.price });
                setCheckoutPlan(null);
                setIsManaging(false);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalSubscription;
